import { ConfirmAction } from "@atlas/composite";
import type { PaginationState } from "@atlas/core";
import {
  Avatar,
  AvatarFallback,
  AvatarWithText,
  Badge,
  Button,
  type CellContext,
  type ColumnDef,
  cn,
  DataGrid,
  DataGridColumnHeader,
  Label,
  Switch,
  ToolbarGroup,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { AdministerUserStatus, type IAdministerUser } from "@models";
import { STANDARD_USERS_KEY, useUpdateUserStatus } from "@query";
import { APP_BASE_PATH, DetailsPanel } from "@shared";
import { useQueryClient } from "@tanstack/react-query";
import { cva } from "class-variance-authority";
import { Eye, Pencil } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";

const userCellVariants = cva("block text-sm truncate", {
  variants: {
    variant: {
      primary: "font-medium",
      secondary: "text-muted-foreground",
    },
    maxWidth: {
      xs: "max-w-[80px]",
      sm: "max-w-[120px]",
      md: "max-w-[180px]",
      lg: "max-w-48",
    },
  },
  defaultVariants: {
    variant: "secondary",
    maxWidth: "md",
  },
});

const DATA_GRID_LAYOUT = {
  columnsVisibility: true,
  headerBackground: true,
  tableBorder: true,
  height: "full",
  headerSticky: true,
} as const;

const ROLE_LABELS: Record<string, string> = {
  COMPANY_ADMIN: "Admin",
  APP_ADMIN: "App Admin",
  STANDARD_USER: "Standard User",
};

interface IUsersProps {
  users: IAdministerUser[];
  companyUuid?: string;
  onEditUser: (user: IAdministerUser) => void;
  totalElements?: number;
  totalPages?: number;
  pagination?: PaginationState;
  onPaginationChange?: (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => void;
  onSearchChange?: (value: string) => void;
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
}

export const Users = memo(function Users({
  users,
  companyUuid,
  onEditUser,
  totalElements = 0,
  totalPages = 0,
  pagination,
  onPaginationChange,
  onSearchChange,
  loading = false,
  loadingMessage,
  emptyMessage,
}: IUsersProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { mutate: updateUserStatus } = useUpdateUserStatus(
    companyUuid,
    APP_BASE_PATH,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [STANDARD_USERS_KEY] });
      },
    },
  );
  const [selectedUser, setSelectedUser] = useState<IAdministerUser | null>(
    null,
  );
  const [statusDialog, setStatusDialog] = useState<{
    user: IAdministerUser;
    isActive: boolean;
  } | null>(null);

  const handleStatusConfirm = useCallback(() => {
    if (!statusDialog) return;
    updateUserStatus(
      {
        userId: statusDialog.user.id,
        isActive: statusDialog.isActive,
      },
      {
        onSuccess: () => {
          setStatusDialog(null);
          toast.add({
            title: t("hub.administer.user_status_success"),
            description: t("hub.administer.user_status_success_description"),
            data: { type: "success" },
          });
        },
        onError: () => {
          toast.add({
            title: t("hub.administer.user_status_error"),
            description: t("hub.administer.user_status_error_description"),
            data: { type: "error" },
          });
        },
      },
    );
  }, [statusDialog, updateUserStatus, toast, t]);
  const headerTextClassName = cn(
    "text-foreground px-1 py-0.5 font-semibold text-sm",
  );
  const statusFilterOptions = useMemo(
    () => [
      {
        label: t("hub.profile.tenant_active"),
        value: AdministerUserStatus.Active,
      },
      {
        label: t("hub.profile.tenant_inactive"),
        value: AdministerUserStatus.Inactive,
      },
    ],
    [t],
  );
  const userColumns = useMemo<ColumnDef<IAdministerUser, unknown>[]>(
    () => [
      {
        accessorKey: "username",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.username")}
            className={headerTextClassName}
          />
        ),
        cell: (info: CellContext<IAdministerUser, unknown>) => (
          <div className="flex min-w-0 items-center gap-3">
            <AvatarWithText name={(info.getValue() as string) ?? ""} />
          </div>
        ),
      },
      {
        accessorKey: "loginId",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.profile.login_id")}
            className={headerTextClassName}
          />
        ),
        cell: (info) => (
          <Label
            className={cn(
              userCellVariants({ variant: "secondary", maxWidth: "md" }),
            )}>
            {(info.getValue() as string) ?? ""}
          </Label>
        ),
      },
      {
        accessorKey: "email",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("auth.login.username_placeholder")}
            className={headerTextClassName}
          />
        ),
        cell: (info) => (
          <Label
            className={cn(
              userCellVariants({ variant: "secondary", maxWidth: "md" }),
            )}>
            {(info.getValue() as string) ?? ""}
          </Label>
        ),
      },
      {
        accessorKey: "joinedDate",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.joined")}
            className={headerTextClassName}
          />
        ),
      },
      {
        accessorKey: "role",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.role")}
            className={headerTextClassName}
          />
        ),
        cell: (info) => {
          const roleValue = info.getValue() as string;
          if (!roleValue) return "";

          return ROLE_LABELS[roleValue] ?? roleValue;
        },
      },
      {
        accessorKey: "application",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.applications")}
            className={headerTextClassName}
          />
        ),
        cell: (info) => (info.getValue() as string) ?? "",
      },
      {
        accessorKey: "status",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.administer.status")}
            className={headerTextClassName}
          />
        ),
        filterFn: (row, columnId, filterValue) => {
          const values = Array.isArray(filterValue) ? filterValue : [];
          if (values.length === 0) return true;
          return values.includes(row.getValue(columnId));
        },
        cell: (info) => (
          <Badge
            variant={
              info.row.original.status === AdministerUserStatus.Active
                ? "success"
                : "neutral"
            }
            appearance="ghost"
            size="sm"
            dot>
            {info.row.original.status}
          </Badge>
        ),
      },
      {
        id: "action",
        header: "",
        enableSorting: false,
        cell: (info) => (
          <ToolbarGroup className="gap-2">
            <Button variant="ghost" size="icon">
              <Eye className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onEditUser(info.row.original);
              }}>
              <Pencil className="size-4" />
            </Button>

            <div className="inline-flex cursor-default border-0 bg-transparent p-0">
              <Switch
                checked={info.row.original.isActive}
                onClick={(e) => e.stopPropagation()}
                onCheckedChange={(checked) => {
                  setStatusDialog({
                    user: info.row.original,
                    isActive: !!checked,
                  });
                }}
              />
            </div>
          </ToolbarGroup>
        ),
      },
    ],
    [onEditUser, t, headerTextClassName],
  );

  const handleRowClick = useCallback((row: IAdministerUser) => {
    setSelectedUser(row);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedUser(null);
  }, []);

  const detailItems = useMemo(() => {
    if (!selectedUser) return [];
    return [
      {
        label: t("hub.administer.first_name"),
        value:
          selectedUser.firstName || selectedUser.username.split(" ")[0] || "—",
      },
      {
        label: t("hub.administer.last_name"),
        value:
          selectedUser.lastName ||
          selectedUser.username.split(" ").slice(1).join(" ") ||
          "—",
      },
      {
        label: t("hub.profile.login_id"),
        value: selectedUser.loginId ?? "—",
      },
      {
        label: t("hub.administer.role"),
        value: selectedUser.role
          ? (ROLE_LABELS[selectedUser.role] ?? selectedUser.role)
          : "—",
      },
      {
        label: t("hub.administer.application"),
        value: selectedUser.application ?? "—",
      },
      {
        label: t("hub.administer.joined"),
        value: selectedUser.joinedDate ?? "—",
      },
      { label: t("hub.administer.status"), value: selectedUser.status ?? "—" },
    ];
  }, [selectedUser, t]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex-1 min-h-0">
        <DataGrid<IAdministerUser>
          data={users}
          columns={userColumns}
          recordCount={totalElements}
          onRowClick={handleRowClick}
          tableLayout={DATA_GRID_LAYOUT}
          withExport
          withSearch={{
            searchKey: "username",
            placeholder: t("hub.administer.search_results"),
            onChange: onSearchChange,
          }}
          withFilter={{
            filters: [
              {
                columnId: "status",
                label: t("hub.administer.status"),
                options: statusFilterOptions,
              },
            ],
          }}
          paginationPlacement="toolbar"
          paginationConfig={{
            showRowsPerPage: true,
          }}
          manualPagination
          pageCount={totalPages}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          loading={loading}
          loadingMessage={loadingMessage ?? t("hub.administer.loading")}
          emptyMessage={emptyMessage ?? t("hub.administer.no_data_available")}
        />

        <DetailsPanel
          open={!!selectedUser}
          onOpenChange={(open) => !open && handleClosePanel()}
          title={t("hub.administer.user_detail")}
          items={detailItems}
          statusLabel={t("hub.administer.status")}
          header={
            selectedUser ? (
              <div className="flex min-w-0 items-center gap-3">
                <Avatar className="size-12 shrink-0">
                  <AvatarFallback>{selectedUser.initial}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label className="truncate">
                        {selectedUser.username}
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="wrap-break-word">{selectedUser.username}</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label className="block truncate text-sm font-normal text-muted-foreground">
                        {selectedUser.email}
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="wrap-break-word">{selectedUser.email}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            ) : undefined
          }
          onEdit={selectedUser ? () => onEditUser(selectedUser) : undefined}
          cancelLabel={t("hub.administer.cancel")}
          editLabel={t("hub.administer.edit")}
        />

        <ConfirmAction
          open={!!statusDialog}
          onOpenChange={(open) => !open && setStatusDialog(null)}
          title={
            statusDialog?.isActive
              ? t("hub.administer.activate_user_title")
              : t("hub.administer.deactivate_user_title")
          }
          description={
            statusDialog?.isActive
              ? t("hub.administer.activate_user_message")
              : t("hub.administer.deactivate_user_message")
          }
          confirmText={
            statusDialog?.isActive
              ? t("hub.administer.activate")
              : t("hub.administer.deactivate")
          }
          cancelText={t("hub.administer.cancel")}
          actionVariant={statusDialog?.isActive ? "default" : "destructive"}
          onConfirm={handleStatusConfirm}
        />
      </div>
    </div>
  );
});
